import { watch, type Ref } from 'vue';
import type { GridApi, ColumnState } from '@ag-grid-community/core';
import { Toast } from '@agyletime/vue-3-common/src/components/Toast';
import { saveCurrentGridSettings, restoreCurrentGridSettings } from '@/components/TeamAndSchedule/utils/agGridUtils';
import { useCSVExport } from '@/components/TeamAndSchedule/composables/useCSVExport';
import { useSessionStorage } from '@agyletime/vue-3-common/src/composables/sessionStorage';
import type { InjectedPagePreferences } from '@agyletime/vue-3-common/src/typings/pagePreference';
import type { ShiftResponseItem } from './teamScheduleHelpers';
import { updateUrlParams } from '../../../utils/urlParamsUtils';
import { formatQueryParamDate } from '@agyletime/vue-3-common/src/utils/date';

interface WatcherParams {
  resetFilters: Ref<boolean>;
  exportCSVTeamAndSchedule: Ref<boolean>;
  saveTeamScheduleGridSettings: Ref<boolean>;
  restoreTeamScheduleGridSettings: Ref<boolean>;
  userTimezone: Ref<string>;
  userTimeFormatPreference: Ref<{ timeFormat: '12h' | '24h' }>;
  pagePreferences: InjectedPagePreferences | undefined;
  gridApi: Ref<GridApi | null>;
  totalTeamSchedule: Ref<number>;
  queryFilter: Ref<Record<string, unknown>>;
  isLoading: Ref<boolean>;
  combinedData: Ref<ShiftResponseItem[]>;
  props: {
    query: Record<string, unknown>;
    groupBy: string;
    colorMode: string;
    columnDisplay: { userId: boolean; email: boolean };
    activeTable: string;
  };
  onResetFilters: () => void;
  onRefreshClientSide: () => Promise<void>;
  onUpdateGridData: (data: ShiftResponseItem[], totalCount: number) => void;
}

// Helper function to update date facet boundaries
function updateDateFacetBoundaries(
    facets: Record<string, Record<string, unknown>>,
    timezone: unknown,
    activeTable: string,
    newQuery: Record<string, unknown>
): void {
    const dateFacet = facets?.dateFacet as { value?: unknown[] } | undefined;
    if (dateFacet?.value?.length === 2 && timezone) {
        const [ minDateStr, maxDateStr ] = JSON.parse(JSON.stringify(dateFacet.value));
        const startDate = formatQueryParamDate(minDateStr as string, timezone as string, 'start');
        const endDate = formatQueryParamDate(maxDateStr as string, timezone as string, 'end');
        (newQuery.facets as Record<string, Record<string, Record<string, unknown>>>)[activeTable].dateFacet.boundaries = `${startDate}_${endDate}`;
    }
}

// Helper function to format boundary from facet values
function formatBoundary(value0: unknown, value1: unknown, timezone: string): string {
    const start = value0 && formatQueryParamDate(value0 as string, timezone);
    const end = value1 && formatQueryParamDate(value1 as string, timezone);
    return start || end ? `${start}_${end}` : '';
}

// Helper function to update tasks start facet boundaries
function updateTasksStartFacetBoundaries(
    facets: Record<string, Record<string, unknown>>,
    timezone: unknown,
    activeTable: string,
    newQuery: Record<string, unknown>
): void {
    const tasksStartFacet = facets?.tasksStartFacet as { value?: unknown[] } | undefined;
    if (!tasksStartFacet?.value || tasksStartFacet.value.length === 0 || !timezone) return;
    
    const boundaries = formatBoundary(tasksStartFacet.value[0], tasksStartFacet.value[1], timezone as string);
    (newQuery.facets as Record<string, Record<string, Record<string, unknown>>>)[activeTable].tasksStartFacet.boundaries = boundaries;
}

// Helper function to update shift start facet boundaries
function updateShiftStartFacetBoundaries(
    facets: Record<string, Record<string, unknown>>,
    timezone: unknown,
    activeTable: string,
    newQuery: Record<string, unknown>
): void {
    const shiftStartFacet = facets?.shiftStartFacet as { value?: unknown[] } | undefined;
    if (!shiftStartFacet?.value || shiftStartFacet.value.length === 0 || !timezone) return;
    
    const boundaries = formatBoundary(shiftStartFacet.value[0], shiftStartFacet.value[1], timezone as string);
    (newQuery.facets as Record<string, Record<string, Record<string, unknown>>>)[activeTable].shiftStartFacet.boundaries = boundaries;
}

// Helper function to handle timezone changes
function handleTimezoneChange(
    newQuery: Record<string, unknown>,
    queryFilter: Ref<Record<string, unknown>>,
    activeTable: string
): void {
    if (queryFilter.value.timezone && queryFilter.value.timezone !== newQuery.timezone) {
        const facets = (newQuery?.facets as Record<string, Record<string, Record<string, unknown>>> | undefined)?.[activeTable];
        if (facets) {
            updateDateFacetBoundaries(facets, newQuery.timezone, activeTable, newQuery);
            updateTasksStartFacetBoundaries(facets, newQuery.timezone, activeTable, newQuery);
            updateShiftStartFacetBoundaries(facets, newQuery.timezone, activeTable, newQuery);
            updateUrlParams({ facets: JSON.stringify(newQuery?.facets) });
        }
    }
}

export function useTeamAndScheduleWatchers(params: WatcherParams): void {
    const {
        resetFilters,
        exportCSVTeamAndSchedule,
        saveTeamScheduleGridSettings,
        restoreTeamScheduleGridSettings,
        userTimeFormatPreference,
        pagePreferences,
        gridApi,
        totalTeamSchedule,
        queryFilter,
        isLoading,
        combinedData,
        props,
        onResetFilters,
        onRefreshClientSide,
        onUpdateGridData
    } = params;

    const sessionStorage = useSessionStorage();

    // Reset filters watcher
    watch(resetFilters, (newValue) => {
        if (newValue) onResetFilters();
    });

    // Export CSV watcher
    watch(exportCSVTeamAndSchedule, async (newValue) => {
        if (!newValue) return;
        const facets = queryFilter?.value?.facets as Record<string, Record<string, unknown>> | undefined;
        const activeFacet = facets?.[props.activeTable] as Record<string, Record<string, unknown>> | undefined;
        const { exportToCSV } = useCSVExport(gridApi, totalTeamSchedule, {
            filePrefix: 'team-schedule',
            successMessage: 'Team schedule exported successfully!',
            errorMessage: 'Failed to export team schedule. Please try again.'
        }, activeFacet ?? {});
        await exportToCSV();
    });

    // Save grid settings watcher
    watch(saveTeamScheduleGridSettings, () => saveCurrentGridSettings(gridApi.value, 'workstreams'));

    // Restore grid settings watcher
    watch(restoreTeamScheduleGridSettings, () => {
        const columnState = sessionStorage.get('workstreams-and-interactions-workstreams-columnState');
        if (columnState && columnState.columnState) {
            restoreCurrentGridSettings(gridApi.value, columnState.columnState as Array<ColumnState>);
            Toast.addToast('restoreCurrentGridSettings', 'Successfully restored grid settings', 'SUCCESS');
        }
    });

    // Query change watcher
    watch<[Record<string, unknown>]>(
        () => [props.query as Record<string, unknown>],
        ([newQuery]) => {
            // Only block API calls during initial load (before first successful fetch)
            // After initial load completes, always allow user-initiated chang
            
            // Skip if query hasn't actually changed (prevents duplicate API calls)
            if (JSON.stringify(newQuery) === JSON.stringify(queryFilter.value)) return;
            
            handleTimezoneChange(newQuery, queryFilter, props.activeTable);
            queryFilter.value = JSON.parse(JSON.stringify(newQuery));
            onRefreshClientSide();
        },
        { deep: true }
    );

    // Page preferences watcher
    watch(
        () => [pagePreferences?.selected.value?.gridPreferences],
        ([newSettings]) => {
            const columnState = (newSettings?.teamSchedule ?? {}) as Record<string, ColumnState>;
            restoreCurrentGridSettings(gridApi.value, Object.values(columnState));
        },
        { deep: true }
    );

    // Group by watcher
    watch(
        () => props.groupBy,
        () => {
            if (isLoading.value) return;
            onUpdateGridData(combinedData.value, totalTeamSchedule.value);
        }
    );

    // Color mode watcher
    watch(
        () => props.colorMode,
        () => {
            if (isLoading.value) return;
            onUpdateGridData(combinedData.value, totalTeamSchedule.value);
        }
    );

    // Column display watcher
    watch(
        () => props.columnDisplay,
        () => {
            if (isLoading.value) return;
            onUpdateGridData(combinedData.value, totalTeamSchedule.value);
        },
        { deep: true }
    );

    // Time format preference watcher
    watch(
        () => userTimeFormatPreference.value,
        (newValue, oldValue) => {
            // Only update if the time format actually changed and we have data
            if (isLoading.value || !combinedData.value.length) return;
            if (newValue?.timeFormat === oldValue?.timeFormat) return;
            
            // Re-transform grid data with new time format
            onUpdateGridData(combinedData.value, totalTeamSchedule.value);
        },
        { deep: true }
    );

}
